import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Smooth, consistent crossfade between media items (images & videos).
 *
 * Key behaviors:
 *  - The next item is PRELOADED off-screen first (image.onLoad / video.canplay).
 *  - The crossfade only starts when the new media is fully ready → no black gap.
 *  - Both images and videos share the same blurred backdrop + object-contain layout
 *    so transitions feel identical regardless of media type.
 *  - The first media also fades in from black on mount (no instant pop-in).
 *  - When a video ends, it stays on screen until the next item is ready,
 *    so the END of one item smoothly bleeds into the START of the next.
 *
 * Props:
 *   item        - current media doc ({id, media_type, ...})
 *   buildSrc    - (item) => string — file URL builder
 *   duration    - ms for the opacity transition (default 900)
 *   onVideoEnded - callback when a video item finishes (advance the playlist)
 *   withBackdrop - true to render a blurred backdrop behind the media (recommended)
 */
export default function CrossfadeMedia({
  item,
  buildSrc,
  duration = 1200,
  onVideoEnded,
  withBackdrop = true,
  startOffsetMs = 0,
}) {
  // Layers stacked on the screen. The first one is mounted hidden, then activated
  // once its onReady fires. The previous layer fades out simultaneously.
  const [layers, setLayers] = useState([]);
  const lastIdRef = useRef(null);

  // When the source item changes, push a new layer (still hidden).
  useEffect(() => {
    if (!item || item.id === lastIdRef.current) return;
    lastIdRef.current = item.id;
    const layerKey = `${item.id}-${Date.now()}`;
    setLayers((prev) => [
      ...prev,
      { key: layerKey, item, visible: false, ready: false, startOffsetMs },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Called by each layer's media element when it's ready to be shown.
  // We then trigger the crossfade and schedule cleanup of stale layers.
  const markReady = useCallback(
    (layerKey) => {
      setLayers((prev) =>
        prev.map((l) => (l.key === layerKey ? { ...l, ready: true, visible: true } : { ...l, visible: false }))
      );
      // Remove layers that have been faded out after the transition completes.
      window.setTimeout(() => {
        setLayers((prev) => prev.filter((l) => l.key === layerKey));
      }, duration + 100);
    },
    [duration]
  );

  return (
    <>
      {layers.map((layer) => (
        <CrossfadeLayer
          key={layer.key}
          layer={layer}
          buildSrc={buildSrc}
          duration={duration}
          withBackdrop={withBackdrop}
          onReady={() => markReady(layer.key)}
          onVideoEnded={onVideoEnded}
        />
      ))}
    </>
  );
}

/* ----------------------------- Layer ----------------------------- */

function CrossfadeLayer({ layer, buildSrc, duration, withBackdrop, onReady, onVideoEnded }) {
  const { item, visible, startOffsetMs = 0 } = layer;
  const isVideo = item.media_type === "video" || /^video\//.test(item.content_type || "");
  const src = buildSrc(item);
  const readyFired = useRef(false);
  const videoRef = useRef(null);
  const seekedRef = useRef(false);

  const fireReadyOnce = () => {
    if (readyFired.current) return;
    readyFired.current = true;
    onReady();
  };

  // Safety net: if onLoad/canplay never fires (network blip), still show the layer
  // after a short timeout so the playlist doesn't stall.
  useEffect(() => {
    const t = window.setTimeout(fireReadyOnce, 4500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seek the video to its wall-clock offset (real-time mirror of the TV).
  const handleVideoMetadata = (e) => {
    if (seekedRef.current) return;
    seekedRef.current = true;
    const v = e.currentTarget;
    const dur = Number.isFinite(v.duration) ? v.duration : 0;
    const offsetSec = (startOffsetMs || 0) / 1000;
    if (dur > 0 && offsetSec > 0 && offsetSec < dur - 0.25) {
      try {
        v.currentTime = offsetSec;
      } catch {
        /* ignore */
      }
    }
  };

  // Common style for the whole layer: smooth opacity transition with shared easing.
  // Symmetric ease-in-out for a soft, cinematic fade between media items.
  const layerStyle = {
    opacity: visible ? 1 : 0,
    transition: `opacity ${duration}ms cubic-bezier(0.45, 0, 0.55, 1)`,
  };

  return (
    <div className="absolute inset-0" style={layerStyle} data-testid="crossfade-layer">
      {/* Backdrop — blurred copy of the same media for a cinematic fill on non-16:9 content */}
      {withBackdrop && !isVideo && (
        <>
          <img
            src={src}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "blur(40px) saturate(1.4)", transform: "scale(1.15)" }}
          />
          <div className="absolute inset-0 bg-black/30" />
        </>
      )}

      {/* Foreground media — same fit/positioning for both images & videos */}
      {isVideo ? (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          playsInline
          controls={false}
          preload="auto"
          onLoadedMetadata={handleVideoMetadata}
          onCanPlay={fireReadyOnce}
          onLoadedData={fireReadyOnce}
          onEnded={onVideoEnded}
          className="absolute inset-0 w-full h-full object-cover bg-black"
        />
      ) : (
        <img
          src={src}
          alt=""
          decoding="async"
          onLoad={fireReadyOnce}
          onError={fireReadyOnce}
          className="absolute inset-0 w-full h-full object-contain bg-black"
        />
      )}
    </div>
  );
}
